import {
  Box,
  Button,
  Card,
  Flex,
  Heading,
  Input,
  Spinner,
  Table,
  Text,
  Textarea,
  VStack,
} from '@chakra-ui/react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { PencilIcon, RefreshCwIcon, SaveIcon, ShieldAlertIcon, XIcon } from 'lucide-react'
import { useEffect, useState } from 'react'
import { PageHeader } from '~/components/shell'
import { useAuth } from '~/contexts/AuthContext'
import { type Setting, settingsApi } from '~/lib/api'
import { toaster } from '~/toaster'

export const Route = createFileRoute('/_app/admin/settings')({
  component: SettingsPage,
})

function SettingsPage() {
  const { hasMinRole, isLoading: authLoading } = useAuth()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [editingKey, setEditingKey] = useState<string | null>(null)
  const [editValue, setEditValue] = useState('')
  const [editDescription, setEditDescription] = useState('')

  const isAdmin = hasMinRole('admin')

  // Redirect non-admins after a short delay
  useEffect(() => {
    if (!authLoading && !isAdmin) {
      const timer = setTimeout(() => {
        navigate({ to: '/' })
      }, 3000)
      return () => clearTimeout(timer)
    }
  }, [authLoading, isAdmin, navigate])

  // Show access denied for non-admins
  if (!authLoading && !isAdmin) {
    return (
      <Flex h="100%" align="center" justify="center">
        <VStack gap={4} textAlign="center" p={8}>
          <Box p={4} borderRadius="full" bg="danger.subtle">
            <ShieldAlertIcon size={48} color="var(--chakra-colors-red-500)" />
          </Box>
          <Heading size="lg" color="danger.fg">
            Access Denied
          </Heading>
          <Text color="content.secondary" maxW="400px">
            This page is restricted to <strong>admin</strong> users only.
          </Text>
          <Text fontSize="sm" color="content.secondary">
            Redirecting to home page...
          </Text>
        </VStack>
      </Flex>
    )
  }

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['settings'],
    queryFn: () => settingsApi.list(),
    retry: false,
  })

  const updateMutation = useMutation({
    mutationFn: ({
      key,
      value,
      description,
    }: {
      key: string
      value: string
      description?: string
    }) => settingsApi.update(key, value, description),
    onSuccess: () => {
      toaster.create({
        title: 'Setting updated',
        type: 'success',
      })
      queryClient.invalidateQueries({ queryKey: ['settings'] })
      setEditingKey(null)
    },
    onError: (error: Error) => {
      toaster.create({
        title: 'Failed to update setting',
        description: error.message,
        type: 'error',
      })
    },
  })

  const startEditing = (setting: Setting) => {
    setEditingKey(setting.key)
    setEditValue(setting.value)
    setEditDescription(setting.description || '')
  }

  const cancelEditing = () => {
    setEditingKey(null)
    setEditValue('')
    setEditDescription('')
  }

  const saveEdit = () => {
    if (editingKey) {
      updateMutation.mutate({
        key: editingKey,
        value: editValue,
        description: editDescription || undefined,
      })
    }
  }

  const settings = data?.settings || []

  return (
    <Box maxW="1400px" mx="auto">
      <PageHeader
        title="System Settings"
        subtitle="Configure system-wide settings and preferences"
        actions={
          <Button size="sm" onClick={() => refetch()}>
            <RefreshCwIcon size={16} />
            Refresh
          </Button>
        }
      />

      <VStack align="stretch" gap={6}>
        <Card.Root>
          <Table.Root>
            <Table.Header>
              <Table.Row bg="bg.subtle">
                <Table.ColumnHeader w="200px">Key</Table.ColumnHeader>
                <Table.ColumnHeader>Value</Table.ColumnHeader>
                <Table.ColumnHeader>Description</Table.ColumnHeader>
                <Table.ColumnHeader w="150px">Last Updated</Table.ColumnHeader>
                <Table.ColumnHeader w="100px">Actions</Table.ColumnHeader>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {isLoading ? (
                <Table.Row>
                  <Table.Cell colSpan={5}>
                    <Flex justify="center" py={8}>
                      <Spinner />
                    </Flex>
                  </Table.Cell>
                </Table.Row>
              ) : settings.length === 0 ? (
                <Table.Row>
                  <Table.Cell colSpan={5}>
                    <Text textAlign="center" py={8} color="content.secondary">
                      No settings found
                    </Text>
                  </Table.Cell>
                </Table.Row>
              ) : (
                settings.map((setting) => (
                  <Table.Row key={setting.key}>
                    <Table.Cell>
                      <Text fontWeight="medium" fontFamily="mono" fontSize="sm">
                        {setting.key}
                      </Text>
                    </Table.Cell>
                    <Table.Cell>
                      {editingKey === setting.key ? (
                        <Textarea
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                          size="sm"
                          rows={2}
                        />
                      ) : (
                        <Text
                          fontSize="sm"
                          fontFamily="mono"
                          whiteSpace="pre-wrap"
                          wordBreak="break-all"
                        >
                          {setting.value}
                        </Text>
                      )}
                    </Table.Cell>
                    <Table.Cell>
                      {editingKey === setting.key ? (
                        <Input
                          value={editDescription}
                          onChange={(e) => setEditDescription(e.target.value)}
                          size="sm"
                          placeholder="Description"
                        />
                      ) : (
                        <Text fontSize="sm" color="content.secondary">
                          {setting.description || '-'}
                        </Text>
                      )}
                    </Table.Cell>
                    <Table.Cell>
                      <Text fontSize="sm">{new Date(setting.updatedAt).toLocaleDateString()}</Text>
                    </Table.Cell>
                    <Table.Cell>
                      {editingKey === setting.key ? (
                        <Flex gap={1}>
                          <Button
                            size="sm"
                            colorPalette="green"
                            onClick={saveEdit}
                            loading={updateMutation.isPending}
                            aria-label="Save setting"
                          >
                            <SaveIcon size={14} />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={cancelEditing}
                            aria-label="Cancel editing"
                          >
                            <XIcon size={14} />
                          </Button>
                        </Flex>
                      ) : (
                        <Button size="sm" variant="ghost" onClick={() => startEditing(setting)}>
                          <PencilIcon size={14} />
                          Edit
                        </Button>
                      )}
                    </Table.Cell>
                  </Table.Row>
                ))
              )}
            </Table.Body>
          </Table.Root>
        </Card.Root>
      </VStack>
    </Box>
  )
}
